import { AddWaiterToOrderModalProps } from '@/renderer/types/sales';
import { Loader2, Search, UserRound, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

const WAITER_ROLE_LABEL = 'waiter';

export const AddWaiterToOrderModal: React.FC<AddWaiterToOrderModalProps> = ({
  isOpen,
  order,
  waiters,
  isLoading = false,
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingWaiterId, setPendingWaiterId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setPendingWaiterId(null);
  }, [isOpen]);

  const filteredWaiters = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return waiters;

    return waiters.filter((waiter) => {
      const fullName = waiter.full_name.toLowerCase();
      const email = (waiter.email || '').toLowerCase();
      const firstName = waiter.first_name.toLowerCase();
      const lastName = waiter.last_name.toLowerCase();
      return (
        fullName.includes(query) ||
        email.includes(query) ||
        firstName.includes(query) ||
        lastName.includes(query)
      );
    });
  }, [searchQuery, waiters]);

  const isBusy = isLoading || isSubmitting;

  const handleSelectWaiter = async (waiterId: string) => {
    if (isBusy || waiterId === order?.waiter_id) return;
    setPendingWaiterId(waiterId);
    await onSubmit({ waiterId });
    setPendingWaiterId(null);
  };

  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isBusy ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isBusy) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close add waiter modal"
      />

      <div className="relative w-full max-w-4xl overflow-hidden rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-black">Add Waiter</h2>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search"
                disabled={isBusy}
                className="h-10 w-44 rounded border border-gray-300 bg-white pl-9 pr-3 text-sm text-black outline-none placeholder:text-gray-400 focus:border-gray-400 focus:ring-2 focus:ring-gray-300"
              />
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isBusy}
              className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-60"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="h-[420px] overflow-y-auto">
          {isLoading ? (
            <div className="flex h-full items-center justify-center gap-2 text-sm text-black/70">
              <Loader2 size={16} className="animate-spin" />
              Loading waiters...
            </div>
          ) : filteredWaiters.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-black/60">
              No waiters found
            </div>
          ) : (
            <div className="grid grid-cols-2 border-t border-l border-gray-200 md:grid-cols-4">
              {filteredWaiters.map((waiter) => {
                const isSelected = waiter.id === order.waiter_id;
                const isCardLoading = pendingWaiterId === waiter.id;
                return (
                  <button
                    key={waiter.id}
                    type="button"
                    onClick={() => handleSelectWaiter(waiter.id)}
                    disabled={isBusy}
                    className={`relative flex h-44 flex-col items-center justify-center gap-2 border-r border-b border-gray-200 p-4 transition-colors ${
                      isSelected
                        ? 'bg-primary text-white'
                        : 'bg-white text-black hover:bg-gray-50'
                    }`}
                  >
                    {isCardLoading ? (
                      <Loader2
                        size={24}
                        className={`animate-spin ${isSelected ? 'text-white' : 'text-black/70'}`}
                      />
                    ) : (
                      <UserRound
                        size={54}
                        className={
                          isSelected ? 'text-white/90' : 'text-sky-500'
                        }
                      />
                    )}
                    <p className="line-clamp-2 text-center text-lg font-medium">
                      {waiter.full_name ||
                        `${waiter.first_name} ${waiter.last_name}`.trim()}
                    </p>
                    <p
                      className={`text-sm lowercase ${isSelected ? 'text-white/90' : 'text-black/60'}`}
                    >
                      {WAITER_ROLE_LABEL}
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
          <p className="text-sm text-black/70">
            BILL #{order.bill_number} • {order.table_name || 'ORDER'}
          </p>
          {isSubmitting ? (
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-black/70">
              <Loader2 size={14} className="animate-spin" />
              Updating waiter...
            </div>
          ) : (
            <p className="text-xs font-medium uppercase tracking-wide text-black/50">
              Select a waiter to update this order
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
